import { Injectable } from "@angular/core";
import { uniq } from "ramda";

const GOOGLE_MAPS_API_KEY = "AIzaSyCDdolPd28awzHFXDs91gJjmrnE50On9YM";

export type Maps = typeof google.maps;

export interface GeoLocationAddress {
  city: string;
  state: string;
  country: string;
  lat: number;
  long: number;
  address: string;
}

@Injectable()
export class GoogleMapService {
  public readonly api = this.load();
  private placeService: google.maps.places.PlacesService;
  private geocoder: google.maps.Geocoder;
  private autocompleteService: google.maps.places.AutocompleteService;

  constructor() {
    this.api.then((maps) => {
      this.placeService = new maps.places.PlacesService(
        document.createElement("div"),
      );
      this.autocompleteService = new maps.places.AutocompleteService();
    });
  }

  // In GoogleMapService
  private load(): Promise<Maps> {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.defer = true;
    const callbackName = `GooglePlaces_cb_` + ((Math.random() * 1e9) >>> 0);
    script.src = this.getScriptSrc(callbackName);

    interface MyWindow {
      [name: string]: (value: unknown) => void;
    }
    const myWindow: MyWindow = window as any;

    const promise = new Promise<Maps>((resolve, reject) => {
      myWindow[callbackName] = () => {
        if (window.google && window.google.maps) {
          resolve(google.maps);
        } else {
          reject(new Error("Google Maps failed to load"));
        }
      };
      script.onerror = (e) => {
        console.error("Script load error:", e);
        reject(new Error("Failed to load Google Maps script"));
      };
    });

    document.body.appendChild(script);
    return promise;
  }

  private getScriptSrc(callback: string): string {
    interface QueryParams {
      [key: string]: string;
    }
    const query: QueryParams = {
      v: "3",
      callback,
      key: GOOGLE_MAPS_API_KEY,
      libraries: "places",
    };
    const params = Object.keys(query)
      .map((key) => `${key}=${query[key]}`)
      .join("&");
    return `https://maps.googleapis.com/maps/api/js?${params}`; // Note the https:// here
  }

  async getLocationDetail(placeId: string) {
    return new Promise<GeoLocationAddress>((resolve, reject) => {
      this.placeService.getDetails(
        {
          placeId: placeId,
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            const fullAddress = place.formatted_address;
            const city = place.address_components.find((c) =>
              c.types.includes("locality"),
            )?.long_name;
            const state = place.address_components.find((c) =>
              c.types.includes("administrative_area_level_1"),
            )?.long_name;
            const country = place.address_components.find((c) =>
              c.types.includes("country"),
            )?.long_name;
            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();

            resolve({
              city,
              state,
              country,
              address: fullAddress,
              lat: latitude,
              long: longitude,
            } as GeoLocationAddress);
          } else {
            reject(new Error("CAN_NOT_GET_PLACE_DETAIL"));
          }
        },
      );
    });
  }

  private async initGeocoder() {
    const maps = await this.api;
    this.geocoder = new maps.Geocoder();
  }

  async getAddressDetails(searchAddress: string): Promise<GeoLocationAddress> {
    if (!this.geocoder) {
      await this.initGeocoder();
    }

    return new Promise((resolve, reject) => {
      this.geocoder.geocode(
        {
          address: searchAddress,
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
            const result = results[0];
            const addressComponents = result.address_components || [];

            const getAddressComponent = (type: string): string => {
              const component = addressComponents.find((c) =>
                c.types.includes(type),
              );
              return component ? component.long_name : "";
            };

            const address: GeoLocationAddress = {
              city: getAddressComponent("locality"),
              state: getAddressComponent("administrative_area_level_1"),
              country: getAddressComponent("country"),
              lat: result.geometry.location.lat(),
              long: result.geometry.location.lng(),
              address: result.formatted_address,
            };

            resolve(address);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        },
      );
    });
  }

  async searchPlaces(
    query: string,
    locationBias?: { center: { lat: number; long: number }; radius: number },
    country?: string,
    placeTypes: string[] = [],
  ) {
    if (!this.autocompleteService) {
      await this.api;
    }

    return new Promise<google.maps.places.AutocompletePrediction[]>(
      (resolve, reject) => {
        const request: google.maps.places.AutocompletionRequest = {
          input: query,
          types: ["tourist_attraction", "point_of_interest"],
        };
        if (locationBias) {
          request.bounds = new google.maps.Circle({
            center: {
              lat: locationBias.center.lat,
              lng: locationBias.center.long,
            },
            radius: locationBias.radius,
          }).getBounds();
        }

        if (country) {
          request.componentRestrictions = {
            country,
          };
        }

        if (placeTypes.length > 0) {
          request.types = placeTypes;
        }

        this.autocompleteService.getPlacePredictions(
          request,
          (predictions, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              resolve(predictions);
            } else if (
              status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
            ) {
              resolve([]);
            } else {
              reject(new Error(`Autocomplete search failed: ${status}`));
            }
          },
        );
      },
    );
  }

  async searchPlacesInCookIslands(query: string, placeTypes: string[] = []) {
    const cookIslandsBias = {
      center: { lat: -21.235909, long: -159.779002 }, // center
      radius: 10000, // 10km radius to cover all the island
    };

    return this.searchPlaces(query, cookIslandsBias, "ck", placeTypes);
  }

  async textSearchInCookIslands(query: string, placeTypes: string[] = []) {
    if (!this.placeService) {
      await this.api;
    }

    return new Promise<google.maps.places.PlaceResult[]>((resolve, reject) => {
      const request: google.maps.places.TextSearchRequest = {
        query: query,
        location: {
          lat: -21.235909,
          lng: -159.779002,
        },
        bounds: new google.maps.Circle({
          center: {
            lat: -21.235909,
            lng: -159.779002,
          },
          radius: 10000,
        }).getBounds(),
      };

      if (placeTypes.length > 0) {
        request.type = placeTypes[0];
      }

      this.placeService.textSearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          resolve(results);
        } else if (
          status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          resolve([]);
        } else {
          reject(new Error(`Text search failed: ${status}`));
        }
      });
    });
  }
}
